import type { Color } from "@mui/material";
import { Stack } from "@mui/material";
import { useEffect, useState } from "react";
import { Provider, useSelector } from "react-redux";
import DataLoadingFailed from "@entrilia/shared/components/DataLoadingFailed.tsx";
import InlineLoader from "@entrilia/shared/components/inlineLoader/InlineLoader.tsx";
import usePageTitle from "@entrilia/shared/hooks/usePageTitle.ts";
import { ApArAgingDetailReportType } from "../../../../../api/types/svTypes.ts";
import DrillDownEntry from "../../sv/components/builder/common/drillDown/DrillDownEntry.tsx";
import { DeveloperPanelWrapper } from "../../sv/components/builder/dev/DeveloperPanelWrapper.tsx";
import { BiCommonDataContextProvider } from "../../sv/contexts/BiCommonDataContextProvider.tsx";
import { selectRefreshToken } from "../../sv/store/selectors/currentReportSelectors.ts";
import {
  selectCurrentDataSetId,
  selectDimensions,
  selectDimensionsStructure,
  selectMeasures,
} from "../../sv/store/selectors/metaDataSelectors.ts";
import { store } from "../../sv/store/store.ts";
import { changeDataSet } from "../../sv/store/thunks/metaDataThunks.ts";
import { DataSetIds } from "../../sv/utilities/DimensionNames.ts";
import { standardReportsByKey } from "../standardReportsDefinitions.tsx";
import AgingDetailView from "./AgingDetailView.tsx";
import { AgingDetailContext } from "./contexts/AgingDetailContext.tsx";
import useAgingDetailState from "./hooks/useAgingDetailState.ts";

const REPORT_ID = "ap-ar-aging-detail";

interface AgingDetailReportConfig {
  title: string;
  loadErrorTitle: string;
  icon: React.ReactNode;
  iconColor: Color;
  reportType: ApArAgingDetailReportType;
}

const createAgingDetailReport = (config: AgingDetailReportConfig) => {
  const AgingDetailReportPage = () => {
    usePageTitle(config.title);

    const state = useAgingDetailState({ reportType: config.reportType });
    const dimensions = useSelector(selectDimensions);
    const dimensionsStructure = useSelector(selectDimensionsStructure);
    const measures = useSelector(selectMeasures);
    const dataSetId = useSelector(selectCurrentDataSetId) ?? DataSetIds.GeneralAndMemo;
    const refreshToken = useSelector(selectRefreshToken);

    if (state.isLoading) {
      return <InlineLoader />;
    }

    if (state.isLoadingFailed) {
      return <DataLoadingFailed title={config.loadErrorTitle} />;
    }

    return (
      <AgingDetailContext value={state}>
        <BiCommonDataContextProvider
          books={[]}
          isMultiBook={false}
          dataSetId={dataSetId}
          dimensionsStructure={dimensionsStructure}
          dimensions={dimensions}
          editMode={false}
          conditions={[]}
          reportId={REPORT_ID}
          refreshToken={refreshToken}
          dimensionNames={dimensions.map((d) => d.name)}
          measureNames={measures.map((m) => m.name)}
          onConditionUpdate={() => {}}
          onRefreshGrid={state.refresh}
        >
          <AgingDetailView
            reportTitle={config.title}
            reportId={REPORT_ID}
            reportIcon={config.icon}
            reportIconColor={config.iconColor}
          />
          <DrillDownEntry reportId={REPORT_ID} />
        </BiCommonDataContextProvider>
      </AgingDetailContext>
    );
  };

  const AgingDetailReportPageWithStore = () => {
    const [isStoreInitialized, setIsStoreInitialized] = useState(false);

    useEffect(() => {
      let isActive = true;

      const initializeStore = async () => {
        await store.dispatch(changeDataSet(DataSetIds.GeneralAndMemo));
        if (isActive) {
          setIsStoreInitialized(true);
        }
      };

      void initializeStore();

      return () => {
        isActive = false;
      };
    }, []);

    if (!isStoreInitialized) {
      return <InlineLoader />;
    }

    return (
      <Provider store={store}>
        <Stack direction="row" spacing={2} sx={{ flex: 1 }}>
          <AgingDetailReportPage />
          <DeveloperPanelWrapper />
        </Stack>
      </Provider>
    );
  };

  return AgingDetailReportPageWithStore;
};

export const ApAgingDetailReportPage = createAgingDetailReport({
  title: "A/P Aging Detail Report",
  loadErrorTitle: "Failed to load A/P aging detail report",
  icon: <standardReportsByKey.apAgingDetail.Icon />,
  iconColor: standardReportsByKey.apAgingDetail.iconColor,
  reportType: ApArAgingDetailReportType.AccountsPayable,
});

export const ArAgingDetailReportPage = createAgingDetailReport({
  title: "A/R Aging Detail Report",
  loadErrorTitle: "Failed to load A/R aging detail report",
  icon: <standardReportsByKey.arAgingDetail.Icon />,
  iconColor: standardReportsByKey.arAgingDetail.iconColor,
  reportType: ApArAgingDetailReportType.AccountsReceivable,
});
